import { makeStyles } from '@material-ui/styles'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Input from 'core/elements/input/Input'
import Tooltip from 'core/elements/tooltip'
import Theme from 'core/themes/model'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { debounce } from 'utils/async'

export interface GridSearchFilterProps {
  value: string
  onChange: (value: string) => void
  label?: string
}

const useStyles = makeStyles<Theme>((theme) => ({
  searchBar: {
    outline: 'none',
    border: 0,
    margin: 0,
    marginRight: theme.spacing(1),
    '& > .inputFrame': {
      maxWidth: 120,
      padding: 0,
      backgroundColor: 'inherit',
      color: 'inherit',
    },
    '& .label, & .input, & .icon': {
      backgroundColor: 'inherit',
      color: 'inherit',
    },
  },
  clearIcon: {
    color: theme.palette.grey[700],
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.grey[900],
    },
    '&:active': {
      color: theme.palette.grey[700],
    },
  },
}))

export default function GridSearchFilter({
  value: initialValue,
  onChange,
  label = 'Search',
}: GridSearchFilterProps) {
  const classes = useStyles()
  const [value, setValue] = useState<string>(initialValue)
  const debouncedUpdateFilterValue = useMemo(() => debounce<[string], void>(onChange), [onChange])

  const handleOnChange = useCallback(
    async ({ target: { value } }) => {
      setValue(value)
      return debouncedUpdateFilterValue(value)
    },
    [debouncedUpdateFilterValue],
  )

  useEffect(() => {
    return () => {
      debouncedUpdateFilterValue.cancel()
    }
  }, [debouncedUpdateFilterValue])

  const renderClearIcon = useMemo(
    () => (
      <Tooltip message="Clear Search">
        <FontAwesomeIcon
          className={classes.clearIcon}
          onClick={() => handleOnChange({ target: { value: '' } })}
          size="md"
          data-testid="clear-search-button"
        >
          times
        </FontAwesomeIcon>
      </Tooltip>
    ),
    [classes.clearIcon, handleOnChange],
  )

  return (
    <Input
      compact
      icon="search"
      placeholder={label}
      className={classes.searchBar}
      value={value !== undefined ? value : ''}
      onChange={handleOnChange}
      type="search"
      endAdornment={value?.length > 0 ? renderClearIcon : null}
    />
  )
}
